import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';

export interface PostHogUser {
    id: string | number;
    firstName: string;
    lastName: string;
    email: string;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class PosthogService {
    private isInitialized = false;
    private readonly POSTHOG_KEY = environment.posthog.key;
    private readonly POSTHOG_HOST = environment.posthog.host;

    constructor() {
        this.init();
        this.setupClickTracking();
    }

    /**
     * Initialize PostHog
     */
    private init(): void {
        if (this.isInitialized) {
            return;
        }

        try {
            posthog.init(this.POSTHOG_KEY, {
                api_host: this.POSTHOG_HOST,
                person_profiles: 'identified_only',
                capture_pageview: false, // We'll handle this manually for SPA
                capture_pageleave: true,
                autocapture: false, // Disabled - we track only clean custom events
                capture_performance: false
            });
            this.isInitialized = true;
        } catch (error) {
            console.error('Error initializing PostHog:', error);
        }
    }

    /**
     * Identify a user in PostHog
     */
    identifyUser(user: PostHogUser): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            const userId = user.id.toString();
            posthog.identify(userId, {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                name: `${user.firstName} ${user.lastName}`
            });
        } catch (error) {
            console.error('Error identifying user in PostHog:', error);
        }
    }

    /**
     * Track a custom event
     */
    trackEvent(eventName: string, properties?: Record<string, any>): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            posthog.capture(eventName, properties);
        } catch (error) {
            console.error('Error tracking event in PostHog:', error);
        }
    }

    /**
     * Setup global click tracking for buttons and links
     */
    private setupClickTracking(): void {
        document.addEventListener('click', (event) => {
            if (!this.isInitialized) return;
            
            const target = event.target as HTMLElement;
            const clickableElement = this.findClickableElement(target);
            
            if (clickableElement) {
                const eventName = this.getClickEventName(clickableElement);
                if (eventName) {
                    this.trackEvent(eventName, {
                        element_type: clickableElement.tagName.toLowerCase(),
                        page_url: window.location.pathname
                    });
                }
            }
        });
    }

    /**
     * Find the nearest clickable element (button, a, input[submit])
     */
    private findClickableElement(element: HTMLElement | null): HTMLElement | null {
        while (element) {
            const tagName = element.tagName?.toLowerCase();
            if (tagName === 'button' || tagName === 'a' || 
                (tagName === 'input' && (element as HTMLInputElement).type === 'submit')) {
                return element;
            }
            element = element.parentElement;
        }
        return null;
    }

    /**
     * Get readable event name from element
     */
    private getClickEventName(element: HTMLElement): string | null {
        const tagName = element.tagName.toLowerCase();
        
        // Get text content (clean it up)
        let text = element.textContent?.trim() || '';
        text = text.replace(/\s+/g, ' ').substring(0, 50); // Clean whitespace, limit length
        
        // Check for aria-label or title as fallback
        const ariaLabel = element.getAttribute('aria-label');
        const title = element.getAttribute('title');
        const dataAction = element.getAttribute('data-action');
        
        // Priority: data-action > aria-label > title > text content
        const label = dataAction || ariaLabel || title || text;
        
        if (!label) return null;
        
        // Create readable event name
        if (tagName === 'button') {
            return `Clicked Button: ${label}`;
        } else if (tagName === 'a') {
            const href = element.getAttribute('href');
            if (href && href !== '#') {
                return `Clicked Link: ${label}`;
            }
            return `Clicked Link: ${label}`;
        } else if (tagName === 'input') {
            return `Clicked Submit: ${label}`;
        }
        
        return null;
    }

    // Route to readable page name mapping
    private readonly PAGE_NAMES: Record<string, string> = {
        '/components/assignments': 'Assignments List',
        '/components/assignments/create-assignment/step1': 'Create Assignment - Step 1',
        '/components/assignments/create-assignment/step2': 'Create Assignment - Step 2',
        '/components/assignments/create-assignment/step3': 'Create Assignment - Step 3',
        '/components/company': 'Company Settings',
        '/components/company/users': 'Company Users',
        '/components/profile': 'User Profile',
        '/components/on-boarding': 'Onboarding',
        '/components/invoices': 'Invoices',
        '/components/guards': 'Guards List',
        '/login': 'Login Page',
        '/register': 'Register Page',
        '/forgot-password': 'Forgot Password',
        '/reset-password': 'Reset Password'
    };

    /**
     * Get readable page name from URL
     */
    private getPageName(url: string): string {
        // Remove query params and hash
        const cleanUrl = url.split('?')[0].split('#')[0];
        
        // Check exact match first
        if (this.PAGE_NAMES[cleanUrl]) {
            return this.PAGE_NAMES[cleanUrl];
        }
        
        // Check partial matches for dynamic routes
        for (const [route, name] of Object.entries(this.PAGE_NAMES)) {
            if (cleanUrl.startsWith(route)) {
                return name;
            }
        }
        
        // Generate readable name from URL path
        const parts = cleanUrl.split('/').filter(p => p && p !== 'components');
        if (parts.length > 0) {
            return parts
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '))
                .join(' - ');
        }
        
        return 'Home';
    }

    /**
     * Track page view (call this on route changes)
     */
    trackPageView(url?: string): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            const currentUrl = url || window.location.pathname;
            const pageName = this.getPageName(currentUrl);
            
            // Use page name directly as event name for clear visibility
            posthog.capture(`Viewed ${pageName}`, {
                page_url: currentUrl,
                $current_url: window.location.href
            });
        } catch (error) {
            console.error('Error tracking page view in PostHog:', error);
        }
    }

    /**
     * Set user properties
     */
    setUserProperties(properties: Record<string, any>): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            posthog.people.set(properties);
        } catch (error) {
            console.error('Error setting user properties in PostHog:', error);
        }
    }

    /**
     * Reset user identity (call on logout)
     */
    reset(): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            posthog.reset();
        } catch (error) {
            console.error('Error resetting PostHog:', error);
        }
    }

    /**
     * Check if a feature flag is enabled
     */
    isFeatureEnabled(flagKey: string): boolean {
        if (!this.isInitialized) {
            return false;
        }

        try {
            return posthog.isFeatureEnabled(flagKey) ?? false;
        } catch (error) {
            console.error('Error checking feature flag in PostHog:', error);
            return false;
        }
    }

    /**
     * Get feature flag value
     */
    getFeatureFlag(flagKey: string): string | boolean | undefined {
        if (!this.isInitialized) {
            return undefined;
        }

        try {
            return posthog.getFeatureFlag(flagKey);
        } catch (error) {
            console.error('Error getting feature flag in PostHog:', error);
            return undefined;
        }
    }

    /**
     * Opt user out of tracking
     */
    optOut(): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            posthog.opt_out_capturing();
        } catch (error) {
            console.error('Error opting out of PostHog:', error);
        }
    }

    /**
     * Opt user back in to tracking
     */
    optIn(): void {
        if (!this.isInitialized) {
            return;
        }

        try {
            posthog.opt_in_capturing();
        } catch (error) {
            console.error('Error opting in to PostHog:', error);
        }
    }
}

